import { Plus, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import gcpAi from '../../../assets/images/gcp-ai-leader.jpg';
import gcpDigital from '../../../assets/images/gcp-digital-leader.png';
import { setOpenCard } from '../../../redux/Slices/CloudPrepSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/store/hooks';

const SET_OPEN_CARD = 'SET_OPEN_CARD';

function GCPCard() {
  const dispatch = useAppDispatch();

  const { openCard } = useAppSelector(state => state.root.cloudPrep);

  const handleToggle = (card: string) => {
    dispatch(setOpenCard(openCard === card ? null : card));
  };

  const navigate = useNavigate();
  const handleClickCloudPractitioner = () => {
    dispatch(setOpenCard('GOOGLE_CLOUD_DIGITAL_LEADER'));
    navigate(
      `/cloud-prep-tabs/${'GOOGLE_CLOUD'}/${'GOOGLE_CLOUD_DIGITAL_LEADER'}`
    );
  };

  const handleClickAIPractitioner = () => {
    dispatch(setOpenCard('GOOGLE_AI_LEADER'));
    navigate(`/cloud-prep-tabs/${'GOOGLE_CLOUD'}/${'GOOGLE_AI_LEADER'}`);
  };

  return (
    <div className='flex flex-col items-center justify-center gap-8 p-6 md:flex-row'>
      {/* ---------- CARD 1 ---------- */}
      <div className='group relative h-[420px] w-80 cursor-pointer'>
        <div className='absolute -inset-1 rounded-2xl bg-gradient-to-r from-orange-400 via-pink-500 via-purple-500 to-blue-500 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-75'></div>
        <div className='relative h-full w-full rounded-2xl bg-white shadow-md'>
          {openCard !== 'cloud' ? (
            <div className='flex flex-col'>
              {/* Image wrapper */}
              <div
                onClick={handleClickCloudPractitioner}
                className='overflow-hidden rounded-lg pt-4'
              >
                <img
                  src={gcpDigital}
                  alt='AWS Certified Cloud Practitioner'
                  className='h-[200px] w-full object-contain transition-transform duration-300 group-hover:scale-110'
                />
              </div>

              <h2
                onClick={handleClickCloudPractitioner}
                className='mt-4 px-6 text-lg font-semibold text-gray-900'
              >
                Cloud Digital Leader
              </h2>
              <p
                onClick={handleClickCloudPractitioner}
                className='mt-2 line-clamp-3 text-ellipsis px-6 text-sm text-gray-600'
              >
                A Cloud Digital Leader can articulate the capabilities of Google
                Cloud core products and services and how they benefit
                organizations. They can also describe common business use cases
                and how cloud solutions support an enterprise.
              </p>
              <div className='mt-auto flex items-center justify-between'>
                {/* Learn More Button (arrow always visible, text slides in) */}
                <div
                  onClick={handleClickCloudPractitioner}
                  className='group relative mt-6 flex w-full items-center justify-start overflow-hidden p-6'
                >
                  {/* Text slides in */}
                  <span className='absolute left-6 -translate-x-4 transform text-sm font-medium text-gray-800 opacity-0 transition-all duration-500 ease-out group-hover:translate-x-0 group-hover:opacity-100'>
                    Learn more
                  </span>

                  {/* Arrow starts at beginning and moves slightly on hover */}
                  <ArrowRight className='mt-[4px] h-4 w-4 text-gray-800 transition-transform duration-500 ease-in-out group-hover:translate-x-20' />
                </div>
                {/* Toggle Button */}
                <div>
                  {openCard !== 'cloud' && (
                    <button
                      onClick={() => handleToggle('cloud')}
                      className='absolute bottom-4 right-4 rounded-full bg-primary p-2 transition hover:bg-blue_a12'
                    >
                      <Plus className='h-5 w-5 text-white' />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className='flex h-full flex-col p-6 text-left'>
              <div className='mb-3 flex items-center justify-between '>
                <h3 className='text-lg font-semibold text-green-700'>
                  About this exam
                </h3>

                <button
                  onClick={() => handleToggle('cloud')}
                  className='rounded-full bg-gray-100 p-2 transition hover:bg-gray-200'
                >
                  {openCard === 'cloud' && (
                    <X className='h-5 w-5 text-gray-800' />
                  )}
                </button>
              </div>
              <p className='mb-6 text-sm leading-relaxed text-gray-700'>
                Cloud Digital Leader validates foundational knowledge of cloud
                concepts, business value, and digital transformation strategies.
                It’s ideal for individuals seeking to understand how cloud
                technology drives innovation and supports organizational goals,
                without requiring technical experience.
              </p>
              <div className='mt-auto'>
                <button className='group mt-auto flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 font-medium text-white transition'>
                  Learn more
                  <ArrowRight className='h-4 w-4 transform transition-transform duration-300' />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---------- CARD 2 ---------- */}
      <div className='group relative h-[420px] w-80 cursor-pointer'>
        {/* Gradient Glow */}
        <div className='absolute -inset-1 rounded-2xl bg-gradient-to-r from-orange-400 via-pink-500 via-purple-500 to-blue-500 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-75'></div>

        {/* White Card */}
        <div className='relative h-full w-full rounded-2xl bg-white shadow-md'>
          {openCard !== 'ai' ? (
            <div className='z-10 flex h-full flex-col'>
              <div
                onClick={handleClickAIPractitioner}
                className='overflow-hidden rounded-t-2xl pt-4'
              >
                <img
                  src={gcpAi}
                  alt='AWS Certified AI Practitioner'
                  className='h-[200px] w-full object-contain transition-transform duration-300 group-hover:scale-110'
                />
              </div>

              <h2
                onClick={handleClickAIPractitioner}
                className='mt-4 px-6 text-lg font-semibold text-gray-900'
              >
                Generative AI Leader
              </h2>
              <p
                onClick={handleClickAIPractitioner}
                className='mt-2 line-clamp-3 text-ellipsis px-6 text-sm text-gray-600'
              >
                A Generative AI Leader is a visionary professional with
                comprehensive knowledge of how generative AI (gen AI) can
                transform businesses. They have business-level knowledge of
                Google Cloud&apos;s gen AI offerings and understand how
                Google&apos;s AI-first approach can lead organizations toward
                innovative and responsible AI adoption. They influence gen
                AI-powered initiatives and identify opportunities across
                business functions and industries, using Google Cloud&apos;s
                enterprise-ready offerings to accelerate innovation.
              </p>
              <div className='mt-auto flex items-center justify-between'>
                {/* Learn More Button */}
                <div
                  onClick={handleClickAIPractitioner}
                  className='group relative mt-5 flex w-full items-center justify-start overflow-hidden p-6'
                >
                  <span className='absolute left-6 -translate-x-4 transform text-sm font-medium text-gray-800 opacity-0 transition-all duration-500 ease-out group-hover:translate-x-0 group-hover:opacity-100'>
                    Learn more
                  </span>
                  <ArrowRight className='mt-[4px] h-4 w-4 text-gray-800 transition-transform duration-500 ease-in-out group-hover:translate-x-20' />
                </div>
              </div>
              <div>
                {/* Toggle Button */}
                {openCard !== 'ai' && (
                  <button
                    onClick={() => handleToggle('ai')}
                    className='absolute bottom-4 right-4 rounded-full bg-primary p-2 transition hover:bg-blue_a12'
                  >
                    <Plus className='h-5 w-5 text-white' />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className='flex h-full flex-col p-6 text-left'>
              <div className='mb-3 flex items-center justify-between'>
                <h3 className='text-lg font-semibold text-green-700'>
                  About this exam
                </h3>
                <button
                  onClick={() => handleToggle('ai')}
                  className='rounded-full bg-gray-100 p-2 transition hover:bg-gray-200'
                >
                  <X className='h-5 w-5 text-gray-800' />
                </button>
              </div>
              <p className='mb-6 text-sm leading-relaxed text-gray-700'>
                Cloud AI Leader validates foundational knowledge of artificial
                intelligence (AI) and machine learning (ML) in cloud
                environments. It’s ideal for individuals seeking to understand
                how AI solutions drive business innovation and digital
                transformation, without requiring technical expertise.
              </p>
              <div className='mt-auto'>
                <button className='group mt-auto flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2 font-medium text-white transition'>
                  Learn more
                  <ArrowRight className='h-4 w-4 transform transition-transform duration-300' />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GCPCard;
